import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router";
import { useDiscordConsent, useMyDiscord, useTeamDiscord, useUnlinkDiscord, useUnlinkDiscordTeam } from "../lib/discord-queries.ts";
import { errorText } from "../lib/errors.ts";
import { Avatar } from "./Avatar.tsx";
import { Icon } from "./Icon.tsx";

/**
 * Settings › Team's Discord card (0.23.0): the server the bot records for, each
 * member's link and consent. The recordings are in Matches › Recordings (0.24.0); a
 * player's own link and consent are in Settings › My account (MyDiscordCard).
 */
export function TeamDiscordCard({ teamId, meId }: { teamId: string; meId: string | undefined }) {
  const { t } = useTranslation();
  const discord = useTeamDiscord(teamId);
  const unlinkTeam = useUnlinkDiscordTeam(teamId);
  const data = discord.data;
  // Links from the bot and the link pages open here.
  const location = useLocation();
  const card = useRef<HTMLElement>(null);
  useEffect(() => {
    if (location.hash === "#discord" && data) card.current?.scrollIntoView({ block: "start" });
  }, [location.hash, data]);

  return (
    <section ref={card} className="card discord-team" id="discord" aria-labelledby="discord-title">
      <h2 id="discord-title" className="eyebrow">
        {t("discord.title")}
      </h2>
      {discord.isPending ? (
        <p className="muted small">{t("common.loading")}</p>
      ) : discord.error || !data ? (
        <p className="field-error small">{errorText(t, discord.error)}</p>
      ) : !data.botReady ? (
        <p className="muted small">{t("discord.off")}</p>
      ) : (
        <>
          {data.guild ? (
            <p className="small">
              {t("discord.server", { name: data.guild.name })}
              {data.canManage && (
                <button
                  type="button"
                  className="link-button discord-unlink"
                  disabled={unlinkTeam.isPending}
                  onClick={() => window.confirm(t("discord.unlinkServerConfirm", { name: data.guild!.name })) && unlinkTeam.mutate()}
                >
                  {t("discord.unlinkServer")}
                </button>
              )}
            </p>
          ) : (
            <ol className="discord-steps small">
              <li>
                {data.inviteUrl ? (
                  <a href={data.inviteUrl} target="_blank" rel="noreferrer">
                    {t("discord.stepInvite")} <Icon name="external" size={12} />
                  </a>
                ) : (
                  t("discord.stepInviteNoLink")
                )}
              </li>
              <li>{t("discord.stepTeam")}</li>
              <li>{t("discord.stepLink")}</li>
              <li>{t("discord.stepRecord")}</li>
            </ol>
          )}

          <ul className="discord-members" aria-label={t("discord.members")}>
            {data.members.map((m) => (
              <li key={m.userId}>
                <Avatar name={m.name} url={m.avatarUrl} me={m.userId === meId} />
                <span className="discord-member-name">{m.name}</span>
                <span className={`discord-state ${m.discordName ? (m.consent ? "is-on" : "is-off") : "is-none"}`}>
                  {m.discordName ? (m.consent ? t("discord.recorded") : t("discord.notRecorded")) : t("discord.noAccount")}
                </span>
              </li>
            ))}
          </ul>

          <p className="muted small">
            {t("discord.recordingsMoved")} <Link to="/matches?view=recordings">{t("discord.recordingsLink")}</Link>
          </p>
        </>
      )}
    </section>
  );
}

/** Settings › My account: my Discord link and whether the bot may record me (0.23.0). */
export function MyDiscordCard() {
  const { t } = useTranslation();
  const mine = useMyDiscord();
  const consent = useDiscordConsent();
  const unlink = useUnlinkDiscord();
  const me = mine.data;
  return (
    <section className="card discord-mine" aria-labelledby="discord-mine-title">
      <h2 id="discord-mine-title" className="eyebrow">
        {t("discord.mineTitle")}
      </h2>
      {mine.isPending ? (
        <p className="muted small">{t("common.loading")}</p>
      ) : (
        <div className="discord-me">
          {me ? (
            <>
              <label className="switch-row">
                <span>
                  <b>{t("discord.myConsent", { name: me.discordName })}</b>
                  <span className="muted small">{t("discord.consentHelp")}</span>
                </span>
                <input type="checkbox" role="switch" className="switch" checked={me.consent} disabled={consent.isPending} onChange={(e) => consent.mutate(e.target.checked)} />
              </label>
              <button type="button" className="link-button small" disabled={unlink.isPending} onClick={() => window.confirm(t("discord.unlinkConfirm")) && unlink.mutate()}>
                {t("discord.unlink")}
              </button>
            </>
          ) : (
            <p className="muted small">{t("discord.notLinkedMine")}</p>
          )}
          {(consent.error || unlink.error) && <p className="field-error small">{errorText(t, consent.error ?? unlink.error)}</p>}
        </div>
      )}
    </section>
  );
}
